import { useAuthStore } from "@features/auth/store/authStore";
import {
  EditorInputField,
  EditorSection,
  EditorTextField,
} from "@shared/components/editors/EditorFields";
import { ImageUploadButtons } from "@shared/components/ImageUploadButtons";
import theme from "@shared/utils/themeColors";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useReducer, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ConfirmationModal from "../../../../components/shared/ConfirmationModal";
import AuthenticatedLayout from "../../../../shared/layouts/AuthenticatedLayout";
import QuestionBankItemHeader from "../../components/QuestionBankItemHeader";
import QuestionBankPassageAdapter from "../../components/QuestionBankPassageAdapter";
import QuestionBankQuestionAdapter from "../../components/QuestionBankQuestionAdapter";
import { CategoryService } from "../../services/category.service";
import { QuestionBankEditorService } from "../../services/question-bank-editor.service";
import {
  QuestionBankItem,
  QuestionBankItemStatus,
  QuestionBankPassage,
  QuestionBankQuestion,
  IframeType,
} from "../../types/question-bank.types";

// Define action types for the reducer
type EditorAction =
  | { type: "SET_ITEM"; payload: QuestionBankItem }
  | { type: "UPDATE_TITLE"; payload: string }
  | { type: "UPDATE_SUBTITLE"; payload: string }
  | {
      type: "UPDATE_INTRODUCTION";
      payload: { title?: string; description?: string };
    }
  | { type: "UPDATE_DIRECTIONS"; payload: string }
  | { type: "UPDATE_IFRAME_CONTENT"; payload: string }
  | { type: "UPDATE_IFRAME_TYPE"; payload: IframeType }
  | { type: "UPDATE_ENABLE_DESMOS_IFRAME"; payload: boolean }
  | { type: "UPDATE_DIFFICULTY"; payload: "easy" | "medium" | "hard" }
  | { type: "UPDATE_TAGS"; payload: string[] }
  | { type: "UPDATE_CATEGORY"; payload: string }
  | {
      type: "UPDATE_PASSAGE";
      payload: { passageIndex: number; passage: QuestionBankPassage };
    }
  | { type: "ADD_PASSAGE"; payload: QuestionBankPassage }
  | { type: "REMOVE_PASSAGE"; payload: number }
  | {
      type: "UPDATE_QUESTION";
      payload: QuestionBankQuestion;
    }
  | { type: "REMOVE_QUESTION" }
  | { type: "UPDATE_STATUS"; payload: QuestionBankItemStatus }
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "SAVE_HISTORY" }
  | { type: "SERVER_SAVE"; payload: QuestionBankItem };

// Define the state type
interface EditorState {
  item: QuestionBankItem | null;
  history: QuestionBankItem[];
  currentHistoryIndex: number;
  isDirty: boolean;
  lastSavedIndex: number; // Track the last index that was saved to the server
}

// Initial state
const initialState: EditorState = {
  item: {
    id: "",
    categoryId: "",
    header: {
      title: "",
      subtitle: "",
    },
    introduction: {
      title: "",
      description: "",
    },
    directions: "",
    iframeContent: "",
    iframeType: IframeType.NONE,
    difficulty: "medium",
    tags: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
    isActive: true,
    status: QuestionBankItemStatus.DRAFT,
    isDeleted: false,
  },
  history: [],
  currentHistoryIndex: -1,
  isDirty: false,
  lastSavedIndex: -1,
};

// Reducer function
function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "SET_ITEM":
      return {
        ...state,
        item: action.payload,
        history: [action.payload],
        currentHistoryIndex: 0,
        isDirty: false,
        lastSavedIndex: 0, // Initial load is considered saved
      };
    case "UPDATE_TITLE":
      if (!state.item) return state;
      const updatedItem = {
        ...state.item,
        header: {
          ...state.item.header,
          title: action.payload,
        },
      };
      return {
        ...state,
        item: updatedItem,
        isDirty: true,
      };
    case "UPDATE_SUBTITLE":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          header: {
            ...state.item.header,
            subtitle: action.payload,
          },
        },
        isDirty: true,
      };
    case "UPDATE_INTRODUCTION":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          introduction: {
            ...state.item.introduction,
            ...action.payload,
          },
        },
        isDirty: true,
      };
    case "UPDATE_DIRECTIONS":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          directions: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_IFRAME_CONTENT":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          iframeContent: action.payload,
          // If the user updates custom content, change type to custom
          iframeType: action.payload ? IframeType.CUSTOM : state.item.iframeType,
        },
        isDirty: true,
      };
    case "UPDATE_IFRAME_TYPE":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          iframeType: action.payload,
          // If user selects NONE or CUSTOM, keep current iframeContent
          // Otherwise, clear it to avoid conflicts with predefined calculators
          ...(action.payload !== IframeType.CUSTOM && action.payload !== IframeType.NONE 
              ? { iframeContent: "" } 
              : {}),
        },
        isDirty: true,
      };
    case "UPDATE_ENABLE_DESMOS_IFRAME":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          enableDesmosIframe: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_DIFFICULTY":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          difficulty: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_TAGS":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          tags: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_CATEGORY":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          categoryId: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_STATUS":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          status: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_PASSAGE":
      if (!state.item || !state.item.passages) return state;
      const updatedPassages = [...state.item.passages];
      updatedPassages[action.payload.passageIndex] = action.payload.passage;
      return {
        ...state,
        item: {
          ...state.item,
          passages: updatedPassages,
        },
        isDirty: true,
      };
    case "ADD_PASSAGE":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          passages: [...(state.item.passages || []), action.payload],
        },
        isDirty: true,
      };
    case "REMOVE_PASSAGE":
      if (!state.item || !state.item.passages) return state;
      const passagesAfterRemoval = [...state.item.passages];
      passagesAfterRemoval.splice(action.payload, 1);
      return {
        ...state,
        item: {
          ...state.item,
          passages: passagesAfterRemoval,
        },
        isDirty: true,
      };
    case "UPDATE_QUESTION":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          question: action.payload,
        },
        isDirty: true,
      };
    case "REMOVE_QUESTION":
      if (!state.item) return state;
      const itemWithoutQuestion = { ...state.item };
      delete itemWithoutQuestion.question;
      return {
        ...state,
        item: itemWithoutQuestion,
        isDirty: true,
      };
    case "SAVE_HISTORY":
      if (!state.item || !state.isDirty) return state;

      // Remove any future history if we've gone back and made changes
      const newHistory = state.history.slice(0, state.currentHistoryIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(state.item)));

      // Keep isDirty true because we still need to save to the server
      // This is just saving to local history
      return {
        ...state,
        history: newHistory,
        currentHistoryIndex: newHistory.length - 1,
        isDirty: true, // Keep it dirty until saved to server
      };
    case "SERVER_SAVE":
      // When we save to the server, we update the item data and mark as not dirty
      // We also need to update the history with the latest server state
      const serverHistory = [...state.history];
      serverHistory[state.currentHistoryIndex] = JSON.parse(
        JSON.stringify(action.payload)
      );

      return {
        ...state,
        item: action.payload,
        history: serverHistory,
        isDirty: false, // Not dirty after server save
        lastSavedIndex: state.currentHistoryIndex, // Update the last saved index
      };
    case "UNDO":
      if (state.currentHistoryIndex <= 0) return state;

      const previousIndex = state.currentHistoryIndex - 1;
      const previousState = state.history[previousIndex];

      // When we undo, we need to check if this state needs to be saved
      // If the current index is different from the last saved index, it needs to be saved
      return {
        ...state,
        item: JSON.parse(JSON.stringify(previousState)),
        currentHistoryIndex: previousIndex,
        isDirty: previousIndex !== state.lastSavedIndex,
      };
    case "REDO":
      if (state.currentHistoryIndex >= state.history.length - 1) return state;

      const nextIndex = state.currentHistoryIndex + 1;
      const nextState = state.history[nextIndex];

      // When we redo, we need to check if this state needs to be saved
      // If the current index is different from the last saved index, it needs to be saved
      return {
        ...state,
        item: JSON.parse(JSON.stringify(nextState)),
        currentHistoryIndex: nextIndex,
        isDirty: nextIndex !== state.lastSavedIndex,
      };
    default:
      return state;
  }
}

// Utility function to preserve scroll position during updates
export function preserveScrollPosition(callback: () => void) {
  // Find the scrollable container by its ID
  const scrollContainer = document.getElementById(
    "question-bank-item-editor-scroll-container"
  );

  // Store current scroll position
  const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

  // Execute the callback that will update state
  callback();

  // Use requestAnimationFrame to restore scroll position after DOM updates
  requestAnimationFrame(() => {
    // Double requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      // Restore scroll position on the specific container
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
      }
    });
  });
}

export default function QuestionBankItemEditor() {
  const { itemId } = useParams<{ itemId: string }>();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<{
    show: boolean;
    passageIndex: number;
  }>({ show: false, passageIndex: -1 });
  const [isSaving, setIsSaving] = useState(false);

  // Check if we're in create mode
  const isCreateMode = !itemId;

  // Initialize category service
  const categoryService = new CategoryService();

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ["question-bank-categories"],
    queryFn: () => categoryService.getCategoryHierarchy(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create a new item if in create mode
  useEffect(() => {
    if (isCreateMode && user) {
      const now = Date.now();
      const newItem: QuestionBankItem = {
        categoryId: "",
        header: {
          title: "",
          subtitle: "",
        },
        introduction: {
          title: "",
          description: "",
        },
        directions: "",
        iframeContent: "",
        iframeType: IframeType.NONE,
        difficulty: "medium",
        tags: [],
        createdAt: now,
        updatedAt: now,
        createdBy: user.uid,
        isActive: true,
        status: QuestionBankItemStatus.DRAFT,
        isDeleted: false,
      };

      dispatch({ type: "SET_ITEM", payload: newItem });
    }
  }, [isCreateMode, user]);

  // Fetch item data only if we're in edit mode
  const {
    data: item,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["question-bank-item-edit", itemId],
    queryFn: () =>
      QuestionBankEditorService.getQuestionBankItemForEditing(itemId!),
    enabled: !isCreateMode,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Initialize state with fetched item
  useEffect(() => {
    if (item) {
      if (!item.question) {
        const now = Date.now();
        const itemWithQuestion = {
          ...item,
          question: {
            id: uuidv4(),
            questionBankItemId: item.id,
            text: "New Question",
            options: {},
            correctAnswer: "",
            createdAt: now,
            updatedAt: now,
          },
        };
        dispatch({ type: "SET_ITEM", payload: itemWithQuestion });
      } else {
        dispatch({ type: "SET_ITEM", payload: item });
      }
    }
  }, [item]);

  // Save history when changes are made
  useEffect(() => {
    if (state.isDirty) {
      const timer = setTimeout(() => {
        // We're only saving to history, not to the server
        // so we use a new action type that doesn't affect isDirty
        dispatch({ type: "SAVE_HISTORY" });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [state.isDirty, state.item]);

  // Effect to handle beforeunload event to prevent accidental window closing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.isDirty) {
        // Standard way to show a confirmation dialog before closing the window
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [state.isDirty]);

  // Handle save
  const handleSave = async () => {
    // Validate that a category is selected
    if (!state.item?.categoryId) {
      toast.error("Please select a category before saving");
      return;
    }

    // Validate that a correct answer is selected
    if (state.item?.question) {
      // Check if the question has options
      const hasOptions = Array.isArray(state.item.question.options)
        ? state.item.question.options.length > 0
        : Object.keys(state.item.question.options).length > 0;

      // Check if a correct answer is selected
      if (hasOptions && !state.item.question.correctAnswer) {
        toast.error(
          "Please select a correct answer for the question before saving"
        );
        return;
      }
    }

    try {
      setIsSaving(true);
      let savedItem;

      if (isCreateMode) {
        // Create new item
        savedItem = await QuestionBankEditorService.createQuestionBankItem(
          state.item
        );

        console.log("Created item:", savedItem);

        // Navigate to edit page for the newly created item
        navigate(`/question-bank/admin/edit/${savedItem.id}`);
      } else {
        // Update existing item
        savedItem = await QuestionBankEditorService.saveQuestionBankItem(
          itemId!,
          state.item
        );
      }

      // Update state with saved item
      dispatch({ type: "SERVER_SAVE", payload: savedItem });
      toast.success("Item saved successfully");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error(error?.error ?? "Failed to save item");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle undo
  const handleUndo = () => {
    dispatch({ type: "UNDO" });
  };

  // Handle redo
  const handleRedo = () => {
    dispatch({ type: "REDO" });
  };

  // Handle item status change
  const handleStatusChange = (isPublished: boolean) => {
    dispatch({
      type: "UPDATE_STATUS",
      payload: isPublished
        ? QuestionBankItemStatus.PUBLISHED
        : QuestionBankItemStatus.DRAFT,
    });
  };

  // Handle passage update
  const handlePassageUpdate = (
    passageIndex: number,
    passage: QuestionBankPassage
  ) => {
    dispatch({
      type: "UPDATE_PASSAGE",
      payload: { passageIndex, passage },
    });
  };

  // Handle passage add
  const handleAddPassage = () => {
    if (!state.item) return;

    const now = Date.now();
    const newPassage: QuestionBankPassage = {
      id: uuidv4(),
      questionBankItemId: state.item.id,
      title: "New Passage",
      lines: [],
      orderIndex: state.item.passages ? state.item.passages.length : 0,
      createdAt: now,
      updatedAt: now,
    };

    dispatch({ type: "ADD_PASSAGE", payload: newPassage });
  };

  // Handle passage remove
  const handleRemovePassage = (passageIndex: number) => {
    setShowDeleteConfirmation({
      show: true,
      passageIndex,
    });
  };

  // Confirm passage delete
  const confirmDeletePassage = async (): Promise<void> => {
    dispatch({
      type: "REMOVE_PASSAGE",
      payload: showDeleteConfirmation.passageIndex,
    });
    setShowDeleteConfirmation({ show: false, passageIndex: -1 });
  };

  // Handle question update
  const handleQuestionUpdate = (question: QuestionBankQuestion) => {
    dispatch({
      type: "UPDATE_QUESTION",
      payload: question,
    });
  };

  // Handle question add
  const handleAddQuestion = () => {
    if (!state.item || state.item.question) return; // Only add if no question exists

    const now = Date.now();
    const newQuestion: QuestionBankQuestion = {
      id: uuidv4(),
      questionBankItemId: state.item.id,
      text: "New Question",
      options: {},
      correctAnswer: "",
      createdAt: now,
      updatedAt: now,
    };

    dispatch({ type: "UPDATE_QUESTION", payload: newQuestion });
  };

  // Handle question remove
  const handleRemoveQuestion = () => {
    dispatch({ type: "REMOVE_QUESTION" });
  };

  // Handle navigation away with unsaved changes
  const handleNavigationAttempt = async (): Promise<void> => {
    // Proceed with navigation
    setShowUnsavedChanges(false);
    navigate("/question-bank/admin/list");
  };

  // Handle directions update
  const handleDirectionsChange = (directions: string) => {
    dispatch({ type: "UPDATE_DIRECTIONS", payload: directions });
  };

  // Handle introduction update
  const handleIntroductionChange = (introduction: {
    title?: string;
    description?: string;
  }) => {
    dispatch({ type: "UPDATE_INTRODUCTION", payload: introduction });
  };

  // Handle difficulty change
  const handleDifficultyChange = (difficulty: "easy" | "medium" | "hard") => {
    dispatch({ type: "UPDATE_DIFFICULTY", payload: difficulty });
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-96 bg-gray-200 rounded mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error || !state.item) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Item Not Found</h2>
            <p className={`mt-2 ${theme.text.muted}`}>
              The question bank item you are trying to edit does not exist or
              you don't have access to it.
            </p>
            <button
              onClick={() => navigate("/question-bank/admin/list")}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-violet-600 hover:bg-violet-700"
            >
              Back to List
            </button>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout defaultCollapsed={true}>
      <div className="flex justify-between mb-4">
        <QuestionBankItemHeader
          item={state.item}
          onChangeStatus={handleStatusChange}
          onSave={handleSave}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onBack={() => handleNavigationAttempt()}
          canUndo={state.currentHistoryIndex > 0}
          canRedo={state.currentHistoryIndex < state.history.length - 1}
          isSaving={isSaving}
          isDirty={state.isDirty}
          categories={categories}
          onCategoryChange={(categoryId) =>
            dispatch({ type: "UPDATE_CATEGORY", payload: categoryId })
          }
          onDifficultyChange={handleDifficultyChange}
        />
      </div>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col space-y-4 p-4`}
        >
          {/* Header Fields */}
          <EditorSection title="Header">
            <EditorInputField
              label="Title"
              value={state.item.header.title}
              onChange={(value) =>
                dispatch({ type: "UPDATE_TITLE", payload: value })
              }
              placeholder="Item title"
            />
            <EditorTextField
              label="Subtitle"
              value={state.item.header.subtitle || ""}
              onChange={(value) =>
                dispatch({ type: "UPDATE_SUBTITLE", payload: value })
              }
              placeholder="Item subtitle"
              rows={2}
            />
          </EditorSection>

          {/* Directions Fields */}
          <EditorSection title="Directions">
            <EditorTextField
              value={state.item.directions || ""}
              onChange={(value) =>
                dispatch({ type: "UPDATE_DIRECTIONS", payload: value })
              }
              placeholder="Directions text"
              rows={3}
            />
            <ImageUploadButtons
              onImageUploaded={(formattedTag) => {
                const updatedText = state.item.directions
                  ? `${state.item.directions}\n${formattedTag}`
                  : formattedTag;

                dispatch({
                  type: "UPDATE_DIRECTIONS",
                  payload: updatedText,
                });
              }}
            />
          </EditorSection>

          {/* Introduction Fields */}
          <EditorSection title="Introduction">
            <EditorInputField
              label="Title"
              value={state.item.introduction?.title || ""}
              onChange={(value) => handleIntroductionChange({ title: value })}
              placeholder="Introduction title"
            />
            <EditorTextField
              label="Description"
              value={state.item.introduction?.description || ""}
              onChange={(value) =>
                handleIntroductionChange({ description: value })
              }
              placeholder="Introduction text"
            />

            <ImageUploadButtons
              onImageUploaded={(formattedTag) => {
                const updatedText = state.item.introduction?.description
                  ? `${state.item.introduction?.description}\n${formattedTag}`
                  : formattedTag;

                handleIntroductionChange({ description: updatedText });
              }}
            />
          </EditorSection>

          {/* IFrame Configuration */}
          <EditorSection title="IFrame Configuration">
            <div className="mb-4">
              <label
                htmlFor="iframe-type"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                IFrame Type
              </label>
              <select
                id="iframe-type"
                value={state.item.iframeType || IframeType.NONE}
                onChange={(e) =>
                  dispatch({
                    type: "UPDATE_IFRAME_TYPE",
                    payload: e.target.value as IframeType,
                  })
                }
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value={IframeType.NONE}>No iFrame</option>
                <option value={IframeType.DESMOS_GRAPHING}>Desmos Graphing</option>
                <option value={IframeType.DESMOS_SCIENTIFIC}>Desmos Scientific</option>
                <option value={IframeType.DESMOS_FOUR_FUNCTION}>Desmos Four Function</option>
                <option value={IframeType.DESMOS_GRAPH}>Desmos Graph</option>
                <option value={IframeType.CUSTOM}>Custom iFrame</option>
              </select>
            </div>

            {state.item.iframeType === IframeType.CUSTOM && (
              <EditorTextField
                label="IFrame HTML"
                value={state.item.iframeContent || ""}
                onChange={(value) =>
                  dispatch({ type: "UPDATE_IFRAME_CONTENT", payload: value })
                }
                placeholder="Paste your iframe HTML code here (example: <iframe src='...' height='450' frameborder='0'></iframe>)"
                rows={6}
              />
            )}
          </EditorSection>

          <div className="flex flex-col space-y-4 items-end">
            {state.item.passages &&
              state.item.passages.map((passage, index) => (
                <QuestionBankPassageAdapter
                  key={passage.id}
                  passage={passage}
                  passageIndex={index}
                  onUpdate={(updatedPassage) =>
                    handlePassageUpdate(index, updatedPassage)
                  }
                  onRemove={() => handleRemovePassage(index)}
                  isExpanded={true}
                  itemId={state.item.id}
                />
              ))}
            <button
              onClick={handleAddPassage}
              className={`${theme.button.primary} w-[140px]`}
            >
              Add Passage
            </button>
          </div>

          <div className="flex flex-col space-y-4 items-end">
            {state.item.question && (
              <>
                <QuestionBankQuestionAdapter
                  question={state.item.question}
                  questionIndex={0}
                  onUpdate={handleQuestionUpdate}
                  onRemove={handleRemoveQuestion}
                  userId={user.uid}
                  isExpanded={true}
                />
                <div className="flex flex-col space-y-4 items-end w-full">
                  <EditorTextField
                    label="Explanation"
                    value={state.item.question.explanation || ""}
                    onChange={(value) => {
                      if (state.item.question) {
                        const updatedQuestion = {
                          ...state.item.question,
                          explanation: value,
                        };
                        dispatch({
                          type: "UPDATE_QUESTION",
                          payload: updatedQuestion,
                        });
                      }
                    }}
                    placeholder="Explanation for the correct answer"
                  />
                  <ImageUploadButtons
                    className="flex items-center w-full"
                    onImageUploaded={(formattedTag) => {
                      const updatedText = state.item.question?.explanation
                        ? `${state.item.question?.explanation}\n${formattedTag}`
                        : formattedTag;

                      const updatedQuestion = {
                        ...state.item.question,
                        explanation: updatedText,
                      };

                      dispatch({
                        type: "UPDATE_QUESTION",
                        payload: updatedQuestion,
                      });
                    }}
                  />
                </div>
              </>
            )}
            {!state.item.question && (
              <button
                onClick={handleAddQuestion}
                className={`${theme.button.primary} w-[140px]`}
              >
                Add Question
              </button>
            )}
          </div>
        </div>
      </div>
      {showDeleteConfirmation.show && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation.show}
          onClose={() =>
            setShowDeleteConfirmation({ show: false, passageIndex: -1 })
          }
          onConfirm={confirmDeletePassage}
          isLoading={false}
          title="Confirm Passage Deletion"
          message="Are you sure you want to delete this passage?"
        />
      )}
      {showUnsavedChanges && (
        <ConfirmationModal
          isOpen={showUnsavedChanges}
          onClose={() => setShowUnsavedChanges(false)}
          onConfirm={handleNavigationAttempt}
          isLoading={false}
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to leave this page?"
        />
      )}
    </AuthenticatedLayout>
  );
}
